import type {ScanContext} from "../scan-model/ScanContext.tsx";
import type {MetlVersionDebug} from "../../../metl-models/debug/MetlVersionDebug.ts";
import type {MetlVersionApi} from "../../../metl-api/MetlVersionApi.ts";
import {flattenMetlJobsTree} from "../../../metl-models/util/MetlJobsTreeFlatten.ts";

export interface VersionId {
    groupName: string
    projectName: string
    versionName: string
}

/**
 * Pick a single representative version from a project's metadata.
 * Prefers "default", then the version with the highest timestamp,
 * then falls back to listing versions directly if no metadata was available.
 */
async function pickSingleVersion(ctx: ScanContext, groupName: string, projectName: string, debugVersions: Record<number, MetlVersionDebug>): Promise<VersionId[]> {
    const {api, log, logError} = ctx
    const versions = Object.values(debugVersions)

    if (versions.some(v => v.name === "default")) {
        return [{ groupName, projectName, versionName: "default" }]
    }

    if (versions.length > 0) {
        const best = versions.reduce((a, b) => a.timestamp > b.timestamp ? a : b)
        return [{ groupName, projectName, versionName: best.name }]
    }

    // No version metadata available — try listing versions directly
    try {
        const versionNames = await api.groups.get(groupName).projects.get(projectName).versions.list()
        if (versionNames.length === 0) {
            log(`Project ${groupName}/${projectName} has no versions, skipping.`)
            return []
        }
        log(`Project ${groupName}/${projectName} had no version metadata, using version list instead.`)
        return [{ groupName, projectName, versionName: versionNames[0] }]
    } catch (e) {
        logError(`Could not retrieve versions for project ${groupName}/${projectName}, skipping this project.`, e)
        return []
    }
}

/** Return a VersionId for every version in a project's metadata. */
function allVersionsFromDebug(groupName: string, projectName: string, debugVersions: Record<number, MetlVersionDebug>): VersionId[] {
    return Object.values(debugVersions).map(version => ({
        groupName,
        projectName,
        versionName: version.name
    }))
}

/**
 * Resolve versions for all projects in a group.
 * Tries to retrieve full metadata first; if that fails, falls back to
 * listing projects and versions individually.
 */
async function identifyVersionsForGroup(ctx: ScanContext, groupName: string, singleVersion: boolean): Promise<VersionId[]> {
    const {api, log, logError} = ctx

    log(`Retrieving metadata of all projects in Group: ${groupName}`)
    try {
        const groupDebug = await api.groups.get(groupName).debug()
        if (!groupDebug.projects) {
            logError(`No projects found in group: [${groupName}], this may indicate project ACLs or a permissions issue. If this persists please try selecting individual projects`)
            return []
        }
        const results = await Promise.all(Object.values(groupDebug.projects).map(async (project): Promise<VersionId[]> => {
            if (singleVersion) {
                return pickSingleVersion(ctx, groupName, project.name, project.versions)
            }
            return allVersionsFromDebug(groupName, project.name, project.versions)
        }))
        return results.flat()
    } catch(debugException) {
        // Metadata retrieval failed — fall back to listing projects and versions individually
        console.error(debugException)
        log(`Could not retrieve full metadata for group ${groupName}, retrieving projects and versions individually instead.`)
        try {
            const projectNames = await api.groups.get(groupName).projects.list()
            const results = await Promise.all(projectNames.map(async (projectName): Promise<VersionId[]> => {
                try {
                    const versionNames = await api.groups.get(groupName).projects.get(projectName).versions.list()
                    if (versionNames.length === 0) return []
                    if (singleVersion) {
                        const defaultVersion = versionNames.find(v => v === "default")
                        return [{ groupName, projectName, versionName: defaultVersion ?? versionNames[0] }]
                    }
                    return versionNames.map(versionName => ({ groupName, projectName, versionName }))
                } catch (e) {
                    logError(`Could not retrieve versions for project ${groupName}/${projectName}, skipping this project.`, e)
                    return []
                }
            }))
            return results.flat()
        } catch (listError) {
            logError(`Could not retrieve projects for group [${groupName}]. This group will be skipped. If this persists please try selecting individual projects.`, listError)
            return []
        }
    }
}

async function identifyAllVersionsForProject(ctx: ScanContext, groupName: string, projectName: string): Promise<VersionId[]> {
    const {api, log, logError} = ctx
    log(`Retrieving metadata of all versions in Project: ${groupName}/${projectName}`)
    try {
        const projectDebug = await api.groups.get(groupName).projects.get(projectName).debug()
        const debugVersions = Object.values(projectDebug.versions ?? {})
        if (debugVersions.length > 0) {
            return debugVersions.map(version => ({groupName, projectName, versionName: version.name}))
        }
        // ACLs can hide version metadata even when the project itself is visible; fall back to listing.
        log(`Project ${groupName}/${projectName} had no version metadata, using version list instead.`)
        const versionNames = await api.groups.get(groupName).projects.get(projectName).versions.list()
        return versionNames.map(versionName => ({groupName, projectName, versionName}))
    } catch (e) {
        logError(`Could not retrieve versions for project ${groupName}/${projectName}, skipping this project.`, e)
        return []
    }
}

async function identifyVersions(ctx: ScanContext): Promise<VersionId[]> {
    const {api, log, logError} = ctx
    const projectOptions = ctx.options.analyseProjects
    log("Identifying versions to scan")

    if(projectOptions.analyzeAll) {
        log("Retrieving list of all project groups")
        const customers = await api.groups.list()
        const singleVersion = projectOptions.analyzeAllSingleVersion ?? true
        const versions = await Promise.all(customers.map(customer =>
            identifyVersionsForGroup(ctx, customer, singleVersion)
        ))
        return versions.flat()
    } else {
        const versions = await Promise.all(projectOptions.projectsToAnalyze.map(async project => {
            if(!project) {
                console.error("Detected empty object in projects to analyze list.")
                return []
            }
            try {
                if(project.analyzeAllVersions) {
                    return identifyAllVersionsForProject(ctx, project.groupName, project.projectName)
                } else {
                    return project.versionsToAnalyze.map(versionName => ({
                        groupName: project.groupName,
                        projectName: project.projectName,
                        versionName
                    }))
                }
            } catch(e) {
                logError(`Could not retrieve versions for project ${project.groupName}/${project.projectName}, skipping this project.`, e)
                return []
            }
        }))
        return versions.flat()
    }
}

export interface VersionScanPlan {
    api: MetlVersionApi
    id: VersionId
    debug: MetlVersionDebug
    usesGit: boolean
    orchestrationJobs: string[]
    transformationJobs: string[]
    jobCount: number
}

export interface VersionsScanPlan {
    totalJobsToScan: number
    versions: VersionScanPlan[]
}

async function planVersionScan(ctx: ScanContext, versions: VersionId[]): Promise<VersionsScanPlan> {
    const {api, log, logError, startAction} = ctx
    const {reportOne, complete} = startAction(`Planning scan of ${versions.length} versions`, versions.length)
    let totalJobCount = 0
    const plan: VersionsScanPlan = {
        totalJobsToScan: 0,
        versions: []
    }
    let anySuccess = false
    for(const version of versions) {
        log(`Planning scan of version ${version.groupName}/${version.projectName}/${version.versionName}`)
        try {
            const versionApi = api.groups.get(version.groupName).projects.get(version.projectName).versions.get(version.versionName)
            const versionDebug = await versionApi.debug()
            const flattenedJobs = flattenMetlJobsTree(versionDebug.jobsTree)

            plan.versions.push({
                api: versionApi,
                id: version,
                debug: versionDebug,
                usesGit: versionDebug.lastCommit !== null,
                orchestrationJobs: flattenedJobs.filter(job => job.type === "ORCHESTRATION").map(job => job.name),
                transformationJobs: flattenedJobs.filter(job => job.type === "TRANSFORMATION").map(job => job.name),
                jobCount: flattenedJobs.length
            })
            totalJobCount += flattenedJobs.length
            anySuccess = true
        } catch(e) {
            logError(`An error occurred while scanning version ${version.groupName}/${version.projectName}/${version.versionName}. This may be due to Project ACLs.`, e)
        }
        reportOne()
    }
    plan.totalJobsToScan = totalJobCount
    if(totalJobCount === 0 || plan.versions.length === 0 || !anySuccess) {
        logError("Version scan has resulted in 0 jobs to scan")
        throw new Error("Version scan has resulted in 0 jobs to scan")
    }
    log(`Identified ${plan.versions.length} versions to scan`)
    log(`Total number of jobs to scan: ${plan.totalJobsToScan}`)
    complete()
    return plan
}

export async function scanVersions(ctx: ScanContext) {
    // This is not wrapped in a try/catch as it is a basic requirement

    const versionsToScan = await identifyVersions(ctx)
    if(versionsToScan.length === 0) {
        throw new Error("No versions have been found to scan. Please try selecting individual versions manually")
    }
    const scanPlan = await planVersionScan(ctx, versionsToScan)
    console.log("Scan plan", scanPlan)
    return scanPlan
}